import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FlowDirection,
  FlowProfile,
  InstrumentLineSubtype,
  LineType,
  InstallationType,
  PnidAIData,
  PnidEquipment,
  PnidNode,
  PrimaryDirection,
  TracingType,
} from '../types';
import SourceDestSelector, { InstrumentRef, RefType } from './SourceDestSelector';

export interface LineMarkPopoverValues {
  line_no: string;
  source_ref: string;
  source_pnid: string;
  dest_ref: string;
  dest_pnid: string;
  line_type?: LineType;
  instrument_line_subtype?: InstrumentLineSubtype | null;
  installation_type?: InstallationType | null;
  is_jacketed?: boolean;
  tracing_type?: TracingType | null;
  flow_profile?: FlowProfile | null;
  slope_ratio?: string | null;
  is_two_phase?: boolean;
  flow_direction?: FlowDirection | null;
  primary_direction?: PrimaryDirection | null;
  drain_type?: string | null;
  is_duct?: boolean;
}

interface LineMarkPopoverProps {
  position: { x: number; y: number };
  title: string;
  stage: 'source' | 'destination';
  values: LineMarkPopoverValues;
  aiData: PnidAIData | null;
  nodes: PnidNode[];
  equipments: PnidEquipment[];
  instruments?: InstrumentRef[];
  currentNodeId?: string;
  pnidOptions?: string[];
  onChange: (patch: Partial<LineMarkPopoverValues>) => void;
  onNext?: () => void;
  onBack?: () => void;
  onFinish?: () => void;
  onClose: () => void;
  // AI detection props
  onDetectSource?: () => void;
  onDetectDest?: () => void;
  isDetectingSource?: boolean;
  isDetectingDest?: boolean;
  aiDetectedSource?: boolean;
  aiDetectedDest?: boolean;
  groqEnabled?: boolean;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '7px 9px',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  fontSize: 12,
  outline: 'none',
};

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: '#374151',
  marginBottom: 4,
};

type PnidAutocompleteOption = { value: string; label?: string };

const PnidAutocompleteInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  onCommit?: () => void;
  options: PnidAutocompleteOption[];
  placeholder?: string;
  inputRef?: React.RefObject<HTMLInputElement>;
}> = ({ value, onChange, onCommit, options, placeholder, inputRef }) => {
  const localRef = useRef<HTMLInputElement>(null);
  const ref = inputRef ?? localRef;

  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);

  const filtered = useMemo(() => {
    const needle = value.trim().toLowerCase();
    const base = options.filter((opt) => opt.value?.trim());
    if (!needle) return base.slice(0, 8);
    return base
      .filter((opt) => {
        const hay = `${opt.label ?? ''} ${opt.value}`.toLowerCase();
        return hay.includes(needle);
      })
      .slice(0, 8);
  }, [options, value]);

  useEffect(() => {
    if (!open) return;
    setHighlightIdx(0);
  }, [open, value]);

  const pick = (opt: PnidAutocompleteOption) => {
    onChange(opt.value);
    setOpen(false);
    setTimeout(() => ref.current?.focus(), 0);
  };

  const commit = () => {
    setOpen(false);
    setTimeout(() => onCommit?.(), 0);
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={inputStyle}
        onFocus={() => {
          if (options.length) setOpen(true);
        }}
        onBlur={() => {
          setTimeout(() => setOpen(false), 120);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setOpen(false);
            return;
          }
          if (e.key === 'ArrowDown') {
            e.preventDefault();
          if (!open) setOpen(true);
          if (!filtered.length) return;
          setHighlightIdx((idx) => Math.min(idx + 1, filtered.length - 1));
          return;
          }
          if (e.key === 'ArrowUp') {
            e.preventDefault();
          if (!open) setOpen(true);
          if (!filtered.length) return;
          setHighlightIdx((idx) => Math.max(idx - 1, 0));
          return;
          }
          if (e.key !== 'Enter' && e.key !== 'Tab') return;
          if (e.key === 'Tab' && e.shiftKey) return;

          e.preventDefault();

          if (open && filtered.length) {
            const candidate = filtered[Math.min(highlightIdx, filtered.length - 1)];
            if (candidate?.value && candidate.value !== value) {
              onChange(candidate.value);
              commit();
              return;
            }
          }

          commit();
        }}
      />

      {open && filtered.length > 0 && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 'calc(100% + 6px)',
            zIndex: 1200,
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            boxShadow: '0 8px 18px rgba(0,0,0,0.08)',
            overflow: 'hidden',
          }}
        >
          {filtered.map((opt, idx) => (
            <div
              key={`${opt.value}-${idx}`}
              onMouseDown={(ev) => ev.preventDefault()}
              onMouseEnter={() => setHighlightIdx(idx)}
              onClick={() => pick(opt)}
              style={{
                padding: '8px 10px',
                fontSize: 12,
                cursor: 'pointer',
                background: idx === highlightIdx ? '#eff6ff' : 'white',
                color: '#111827',
                borderBottom: idx === filtered.length - 1 ? 'none' : '1px solid #f3f4f6',
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 12, color: '#111827' }}>{opt.label ?? opt.value}</div>
              {opt.label && opt.label !== opt.value && (
                <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>{opt.value}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const LineMarkPopover: React.FC<LineMarkPopoverProps> = ({
  position,
  title,
  stage,
  values,
  aiData,
  nodes,
  equipments,
  instruments = [],
  currentNodeId,
  pnidOptions = [],
  onChange,
  onNext,
  onBack,
  onFinish,
  onClose,
  onDetectSource,
  onDetectDest,
  isDetectingSource = false,
  isDetectingDest = false,
  aiDetectedSource = false,
  aiDetectedDest = false,
  groqEnabled = false,
}) => {
  const dialogWidth = 360;
  const padding = 16;
  type WizardStepId = 'source_ref' | 'source_pnid' | 'dest_ref' | 'dest_pnid' | 'submit';

  const computed = useMemo(() => {
    let left = position.x + 16;
    let top = position.y + 10;

    if (left + dialogWidth > window.innerWidth - padding) {
      left = Math.max(padding, position.x - dialogWidth - 16);
    }
    if (top < padding) top = padding;
    if (top + 320 > window.innerHeight - padding) {
      top = Math.max(padding, window.innerHeight - padding - 320);
    }

    return { left, top };
  }, [position.x, position.y]);

  const hasDraggedRef = useRef(false);
  const [panelPos, setPanelPos] = useState(() => computed);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<{ startX: number; startY: number; startLeft: number; startTop: number } | null>(null);

  useEffect(() => {
    if (hasDraggedRef.current) return;
    setPanelPos(computed);
  }, [computed.left, computed.top]);

  const lineNoRef = useRef<HTMLInputElement>(null);
  const sourcePnidRef = useRef<HTMLInputElement>(null);
  const destPnidRef = useRef<HTMLInputElement>(null);
  const [wizardStep, setWizardStep] = useState<WizardStepId>(stage === 'source' ? 'source_ref' : 'dest_ref');

  useEffect(() => {
    setWizardStep(stage === 'source' ? 'source_ref' : 'dest_ref');
  }, [stage]);

  const pnidAutocompleteOptions = useMemo<PnidAutocompleteOption[]>(() => {
    const result: PnidAutocompleteOption[] = [];
    const seen = new Set<string>();

    const current = pnidOptions[0]?.trim();
    if (current && !seen.has(current)) {
      seen.add(current);
      result.push({ value: current, label: `${current} (Current)` });
    }

    for (const tieIn of aiData?.tie_ins ?? []) {
      const id = tieIn.tie_in_id?.trim();
      if (!id || seen.has(id)) continue;
      seen.add(id);
      result.push({ value: id, label: `${tieIn.direction}: ${tieIn.service || 'Tie-in'}` });
    }

    return result;
  }, [pnidOptions, aiData?.tie_ins]);

  const goPrev = () => {
    if (stage === 'destination' && wizardStep === 'dest_ref') {
      onBack?.();
      return;
    }
    setWizardStep((prev) => {
      if (stage === 'source') return prev === 'source_pnid' ? 'source_ref' : 'source_ref';
      if (prev === 'submit') return 'dest_pnid';
      if (prev === 'dest_pnid') return 'dest_ref';
      return 'dest_ref';
    });
  };

  const goNext = () => {
    setWizardStep((prev) => {
      if (stage === 'source') {
        if (prev === 'source_ref') return 'source_pnid';
        onNext?.();
        return 'source_pnid';
      }

      if (prev === 'dest_ref') return 'dest_pnid';
      if (prev === 'dest_pnid') return 'submit';
      onFinish?.();
      return 'submit';
    });
  };

  const stepTitle = useMemo(() => {
    if (wizardStep === 'source_ref' || wizardStep === 'source_pnid') return 'Confirm SOURCE';
    if (wizardStep === 'dest_ref' || wizardStep === 'dest_pnid') return 'Confirm DESTINATION';
    return 'Review & Submit';
  }, [wizardStep]);

  const stepCounter = useMemo(() => {
    if (stage === 'source') return wizardStep === 'source_ref' ? 'Step 1/2' : 'Step 2/2';
    if (wizardStep === 'dest_ref') return 'Step 1/3';
    if (wizardStep === 'dest_pnid') return 'Step 2/3';
    return 'Step 3/3';
  }, [stage, wizardStep]);

  useEffect(() => {
    const focusLater = (el: HTMLInputElement | null) => {
      if (!el) return;
      // Let the DOM settle so focus works reliably on mount/stage switch
      setTimeout(() => el.focus(), 0);
    };

    if (wizardStep === 'source_pnid') {
      focusLater(sourcePnidRef.current);
      return;
    }

    if (wizardStep === 'dest_pnid') {
      focusLater(destPnidRef.current);
      return;
    }

    if (wizardStep === 'submit') {
      focusLater(lineNoRef.current);
    }
  }, [wizardStep]);

  return (
    <div
      tabIndex={-1}
      onKeyDownCapture={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          onClose();
          return;
        }

        if (e.key === 'Enter' && e.shiftKey) {
          e.preventDefault();
          goPrev();
          return;
        }

        if (e.ctrlKey && e.key.toLowerCase() === 'l') {
          e.preventDefault();
          lineNoRef.current?.focus();
        }
      }}
      style={{
        position: 'fixed',
        left: panelPos.left,
        top: panelPos.top,
        zIndex: 1100,
        width: dialogWidth,
        background: 'white',
        borderRadius: 10,
        boxShadow: '0 8px 32px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.05)',
        overflow: 'visible',
      }}
    >
      <div
        style={{
          padding: '10px 12px',
          borderBottom: '1px solid #e5e7eb',
          background: '#eff6ff',
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
        }}
        onPointerDown={(e) => {
          if (e.button !== 0) return;
          const target = e.target as HTMLElement | null;
          if (target?.closest('[data-no-drag]')) return;

          hasDraggedRef.current = true;
          setIsDragging(true);
          dragRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            startLeft: panelPos.left,
            startTop: panelPos.top,
          };

          (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
          e.preventDefault();
        }}
        onPointerMove={(e) => {
          if (!isDragging) return;
          if (!dragRef.current) return;

          const dx = e.clientX - dragRef.current.startX;
          const dy = e.clientY - dragRef.current.startY;

          const nextLeft = dragRef.current.startLeft + dx;
          const nextTop = dragRef.current.startTop + dy;

          // Keep the dialog within the viewport with padding.
          const clampedLeft = Math.min(
            window.innerWidth - padding - dialogWidth,
            Math.max(padding, nextLeft)
          );
          const clampedTop = Math.min(
            window.innerHeight - padding - 80,
            Math.max(padding, nextTop)
          );

          setPanelPos({ left: clampedLeft, top: clampedTop });
          e.preventDefault();
        }}
        onPointerUp={(e) => {
          if (!isDragging) return;
          setIsDragging(false);
          dragRef.current = null;
          try {
            (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
          } catch {
            // ignore
          }
        }}
        onPointerCancel={() => {
          setIsDragging(false);
          dragRef.current = null;
        }}
      >
        <div>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#1f2937' }}>{title}</div>
          <div style={{ fontSize: 10, color: '#4b5563', marginTop: 2 }}>
            {stepCounter} · {stepTitle}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          data-no-drag="true"
          style={{
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            fontSize: 16,
            lineHeight: 1,
            color: '#6b7280',
          }}
          aria-label="Close"
          title="Close"
        >
          ×
        </button>
      </div>

      <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {wizardStep === 'source_ref' && (
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: '#111827' }}>Source Ref</div>
              <div style={{ fontSize: 10, color: '#6b7280' }}>Start</div>
            </div>

            <div style={{ marginTop: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <div style={labelStyle}>Source</div>
                {aiDetectedSource && (
                  <span style={{ fontSize: 9, color: '#059669', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <span>✓</span> AI detected
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <SourceDestSelector
                    label="Source"
                    value={values.source_ref}
                    onChange={(val: string, refType, externalRef) => {
                      const patch: Partial<LineMarkPopoverValues> = { source_ref: val };
                      if (refType === 'external' && externalRef?.pnid) {
                        patch.source_pnid = externalRef.pnid;
                      }
                      onChange(patch);
                    }}
                    aiData={aiData}
                    nodes={nodes}
                    equipments={equipments}
                    instruments={instruments}
                    includeDrain={false}
                    currentNodeId={currentNodeId}
                    forceExpanded={true}
                    autoFocusSearch={true}
                    onAdvance={goNext}
                  />
                </div>
                {groqEnabled && onDetectSource && (
                  <button
                    type="button"
                    onClick={onDetectSource}
                    disabled={isDetectingSource}
                    data-no-drag="true"
                    style={{
                      padding: '6px 8px',
                      fontSize: 10,
                      fontWeight: 600,
                      borderRadius: 6,
                      border: '1px solid #d1d5db',
                      background: isDetectingSource ? '#f3f4f6' : 'white',
                      color: isDetectingSource ? '#9ca3af' : '#6366f1',
                      cursor: isDetectingSource ? 'not-allowed' : 'pointer',
                      whiteSpace: 'nowrap',
                      height: 30,
                      alignSelf: 'flex-start',
                      marginTop: 18,
                    }}
                    title="Detect with AI"
                  >
                    {isDetectingSource ? '...' : '🎯 AI'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {wizardStep === 'source_pnid' && (
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: '#111827' }}>Source P&amp;ID</div>
              <div style={{ fontSize: 10, color: '#6b7280' }}>Start</div>
            </div>

            <div style={{ marginTop: 8 }}>
              <PnidAutocompleteInput
                inputRef={sourcePnidRef}
                value={values.source_pnid}
                onChange={(val) => onChange({ source_pnid: val })}
                placeholder="Select or type P&ID..."
                options={pnidAutocompleteOptions}
                onCommit={goNext}
              />
            </div>

            <div style={{ marginTop: 10, fontSize: 11, color: '#6b7280' }}>
              Source: <strong>{values.source_ref || '(not set)'}</strong>
            </div>
          </div>
        )}

        {wizardStep === 'dest_ref' && (
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: '#111827' }}>Destination Ref</div>
              <div style={{ fontSize: 10, color: '#6b7280' }}>End</div>
            </div>

            <div style={{ marginTop: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <div style={labelStyle}>Destination</div>
                {aiDetectedDest && (
                  <span style={{ fontSize: 9, color: '#059669', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <span>✓</span> AI detected
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <SourceDestSelector
                    label="Destination"
                    value={values.dest_ref}
                    onChange={(val: string, refType: RefType, externalRef) => {
                      if (refType === 'drain') {
                        onChange({ dest_ref: val, drain_type: val });
                      } else {
                        const patch: Partial<LineMarkPopoverValues> = { dest_ref: val, drain_type: null };
                        if (refType === 'external' && externalRef?.pnid) {
                          patch.dest_pnid = externalRef.pnid;
                        }
                        onChange(patch);
                      }
                    }}
                    aiData={aiData}
                    nodes={nodes}
                    equipments={equipments}
                    instruments={instruments}
                    includeDrain={true}
                    currentNodeId={currentNodeId}
                    forceExpanded={true}
                    autoFocusSearch={true}
                    onAdvance={goNext}
                  />
                </div>
                {groqEnabled && onDetectDest && (
                  <button
                    type="button"
                    onClick={onDetectDest}
                    disabled={isDetectingDest}
                    data-no-drag="true"
                    style={{
                      padding: '6px 8px',
                      fontSize: 10,
                      fontWeight: 600,
                      borderRadius: 6,
                      border: '1px solid #d1d5db',
                      background: isDetectingDest ? '#f3f4f6' : 'white',
                      color: isDetectingDest ? '#9ca3af' : '#6366f1',
                      cursor: isDetectingDest ? 'not-allowed' : 'pointer',
                      whiteSpace: 'nowrap',
                      height: 30,
                      alignSelf: 'flex-start',
                      marginTop: 18,
                    }}
                    title="Detect with AI"
                  >
                    {isDetectingDest ? '...' : '🎯 AI'}
                  </button>
                )}
              </div>
            </div>

            <div style={{ marginTop: 10, fontSize: 11, color: '#6b7280' }}>
              Source: <strong>{values.source_ref || '(not set)'}</strong>
            </div>
          </div>
        )}

        {wizardStep === 'dest_pnid' && (
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: '#111827' }}>Destination P&amp;ID</div>
              <div style={{ fontSize: 10, color: '#6b7280' }}>End</div>
            </div>

            <div style={{ marginTop: 8 }}>
              <PnidAutocompleteInput
                inputRef={destPnidRef}
                value={values.dest_pnid}
                onChange={(val) => onChange({ dest_pnid: val })}
                placeholder="Select or type P&ID..."
                options={pnidAutocompleteOptions}
                onCommit={goNext}
              />
            </div>

            <div style={{ marginTop: 10, fontSize: 11, color: '#6b7280' }}>
              Destination: <strong>{values.dest_ref || '(not set)'}</strong>
            </div>
          </div>
        )}

        {wizardStep === 'submit' && (
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 12, color: '#111827', marginBottom: 8 }}>Review</div>
            <div style={{ fontSize: 12, color: '#111827', lineHeight: 1.4 }}>
              <div>
                <strong>Source:</strong> {values.source_ref || '(not set)'} · {values.source_pnid || '(not set)'}
              </div>
              <div style={{ marginTop: 6 }}>
                <strong>Destination:</strong> {values.dest_ref || '(not set)'} · {values.dest_pnid || '(not set)'}
              </div>
            </div>

            <div style={{ marginTop: 10 }}>
              <div style={labelStyle}>Line No (optional)</div>
              <input
                ref={lineNoRef}
                value={values.line_no}
                onChange={(e) => onChange({ line_no: e.target.value })}
                placeholder="Line number"
                style={inputStyle}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter' && e.key !== 'Tab') return;
                  if (e.key === 'Tab' && e.shiftKey) return;
                  e.preventDefault();
                  goNext();
                }}
              />
              <div style={{ marginTop: 6, fontSize: 10, color: '#6b7280' }}>Tip: `Ctrl+L` focuses this anytime.</div>
            </div>
          </div>
        )}
      </div>

      <div
        style={{
          padding: '10px 12px',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 10,
          background: '#f9fafb',
          borderBottomLeftRadius: 10,
          borderBottomRightRadius: 10,
        }}
      >
        <div style={{ fontSize: 11, color: '#6b7280' }}>
          Enter/Tab = confirm · Shift+Enter = back · Esc = cancel
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {stage === 'destination' && (
            <button
              type="button"
              onClick={() => {
                if (wizardStep === 'dest_ref') {
                  onBack?.();
                  return;
                }
                goPrev();
              }}
              style={{
                padding: '6px 10px',
                fontSize: 12,
                fontWeight: 600,
                borderRadius: 8,
                border: '1px solid #d1d5db',
                background: 'white',
                color: '#374151',
                cursor: 'pointer',
              }}
            >
              Back
            </button>
          )}
          {wizardStep === 'submit' && (
            <button
              type="button"
              onClick={() => goNext()}
              style={{
                padding: '6px 10px',
                fontSize: 12,
                fontWeight: 700,
                borderRadius: 8,
                border: 'none',
                background: '#2563eb',
                color: 'white',
                cursor: 'pointer',
              }}
            >
              Submit
            </button>
          )}
        </div>
      </div>

    </div>
  );
};

export default LineMarkPopover;
